import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Image as ImageIcon,
  Upload,
  GripVertical,
  ExternalLink,
  Check,
  Download,
  Search,
} from 'lucide-react'
import { supabase, uploadFile, deleteFile, getPublicUrl } from '@/lib/supabase'
import { RichTextEditor } from '@/components/admin/RichTextEditor'
import type { Category, Product, FilterDefinition } from '@/types'

const productSchema = z.object({
  code: z.string().min(1, 'Product code is required'),
  slug: z.string().min(1, 'Slug is required'),
  name: z.string().min(1, 'Product name is required'),
  category_id: z.string().min(1, 'Category is required'),
  description: z.string().optional(),
  is_active: z.boolean(),
  sort_order: z.number(),
  features: z.array(z.object({
    feature: z.string().min(1),
    sort_order: z.number(),
  })),
})

type ProductFormData = z.infer<typeof productSchema>

// Sortable Feature Item Component
interface SortableFeatureItemProps {
  id: string
  idx: number
  register: ReturnType<typeof useForm<ProductFormData>>['register']
  onRemove: (idx: number) => void
  isModified: boolean
  onSave: (idx: number) => void
}

// Sortable Image Item Component
interface SortableImageItemProps {
  id: string
  image: { id?: string; url: string; is_primary: boolean; file?: File }
  index: number
  onSetPrimary: (index: number) => void
  onDownload: (url: string, filename: string) => void
  onRemove: (index: number) => void
}

function SortableImageItem({
  id,
  image,
  index,
  onSetPrimary,
  onDownload,
  onRemove,
}: SortableImageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
        image.is_primary ? 'border-primary-500' : 'border-secondary-200'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <img src={image.url} alt="" className="w-full h-full object-cover" />
      {/* Drag handle overlay */}
      <div
        {...attributes}
        {...listeners}
        className="absolute inset-0 cursor-grab active:cursor-grabbing"
      />
      {/* Action buttons on hover */}
      <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onSetPrimary(index)
          }}
          className="p-2 bg-white rounded-full pointer-events-auto"
          title="Set as primary"
        >
          <ImageIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onDownload(image.url, `product-image-${index + 1}.jpg`)
          }}
          className="p-2 bg-white rounded-full text-blue-500 pointer-events-auto"
          title="Download image"
        >
          <Download className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRemove(index)
          }}
          className="p-2 bg-white rounded-full text-red-500 pointer-events-auto"
          title="Delete image"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {image.is_primary && (
        <span className="absolute top-2 left-2 badge bg-primary-600 text-white text-xs pointer-events-none">
          Primary
        </span>
      )}
      {/* Drag indicator */}
      <div className="absolute bottom-2 right-2 p-1 bg-white/80 rounded pointer-events-none">
        <GripVertical className="w-4 h-4 text-secondary-400" />
      </div>
    </div>
  )
}

function SortableFeatureItem({
  id,
  idx,
  register,
  onRemove,
  isModified,
  onSave,
}: SortableFeatureItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-2 ${isDragging ? 'z-50' : ''}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="p-1 mt-2 cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="w-5 h-5 text-secondary-400" />
      </button>
      <textarea
        {...register(`features.${idx}.feature`)}
        data-feature-input
        rows={2}
        className="input flex-1 resize-none min-h-[60px]"
        placeholder="Feature description (press Enter for new line)"
      />
      {isModified ? (
        <button
          type="button"
          onClick={() => onSave(idx)}
          className="p-2 mt-1 text-green-600 hover:bg-green-50 rounded-lg"
          title="Mark as saved"
        >
          <Check className="w-4 h-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onRemove(idx)}
          className="p-2 mt-1 text-red-500 hover:bg-red-50 rounded-lg"
          title="Remove feature"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

export function ProductFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditing = id && id !== 'new'

  const [images, setImages] = useState<{ id?: string; url: string; is_primary: boolean; file?: File }[]>([])
  const [imagesToDelete, setImagesToDelete] = useState<{ id: string; url: string }[]>([])
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({})
  const [originalFeatures, setOriginalFeatures] = useState<Record<number, string>>({})

  // SEO fields
  const [seoData, setSeoData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image: '',
  })

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order')
      if (error) throw error
      return data as Category[]
    },
  })

  // Fetch filter definitions
  const { data: filterDefinitions } = useQuery({
    queryKey: ['filter-definitions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('filter_definitions')
        .select('*')
        .order('sort_order')
      if (error) throw error
      return data as FilterDefinition[]
    },
  })

  // Fetch existing product
  const { data: existingProduct } = useQuery({
    queryKey: ['admin-product', id],
    queryFn: async () => {
      if (!isEditing) return null
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          images:product_images(*),
          features:product_features(*),
          filters:product_filters(*)
        `)
        .eq('id', id)
        .single()
      if (error) throw error
      return data as Product
    },
    enabled: !!isEditing,
  })

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      code: '',
      slug: '',
      name: '',
      category_id: '',
      description: '',
      is_active: true,
      sort_order: 0,
      features: [],
    },
  })

  const { fields: featureFields, append: appendFeature, remove: removeFeature } = useFieldArray({
    control,
    name: 'features',
  })

  // Populate form when editing - wait for both existingProduct and categories to be loaded
  useEffect(() => {
    if (existingProduct && categories) {
      reset({
        code: existingProduct.code,
        slug: existingProduct.slug,
        name: existingProduct.name,
        category_id: existingProduct.category_id,
        description: existingProduct.description || '',
        is_active: existingProduct.is_active,
        sort_order: existingProduct.sort_order,
        features: existingProduct.features?.map((f) => ({
          feature: f.feature,
          sort_order: f.sort_order,
        })) || [],
      })

      // Set images - sorted by sort_order
      const sortedImages = [...(existingProduct.images || [])]
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((img) => ({
          id: img.id,
          url: img.image_url,
          is_primary: img.is_primary,
        }))
      setImages(sortedImages)

      // Set filters
      const filters: Record<string, string[]> = {}
      existingProduct.filters?.forEach((f) => {
        if (!filters[f.filter_type]) filters[f.filter_type] = []
        filters[f.filter_type].push(f.filter_value)
      })
      setSelectedFilters(filters)

      // Set original features for change detection
      const origFeatures: Record<number, string> = {}
      existingProduct.features?.forEach((f, idx) => {
        origFeatures[idx] = f.feature
      })
      setOriginalFeatures(origFeatures)

      // Set SEO data
      setSeoData({
        meta_title: existingProduct.meta_title || '',
        meta_description: existingProduct.meta_description || '',
        meta_keywords: existingProduct.meta_keywords || '',
        og_image: existingProduct.og_image || '',
      })
    }
  }, [existingProduct, categories, reset])

  // Auto-generate slug from name
  const name = watch('name')
  useEffect(() => {
    if (!isEditing && name) {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      setValue('slug', slug)
    }
  }, [name, isEditing, setValue])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      let productId = id

      // Create or update product
      if (isEditing) {
        const { error } = await supabase
          .from('products')
          .update({
            code: data.code,
            slug: data.slug,
            name: data.name,
            category_id: data.category_id,
            description: data.description || null,
            is_active: data.is_active,
            sort_order: data.sort_order,
            meta_title: seoData.meta_title || null,
            meta_description: seoData.meta_description || null,
            meta_keywords: seoData.meta_keywords || null,
            og_image: seoData.og_image || null,
          })
          .eq('id', id)
        if (error) throw error
      } else {
        const { data: newProduct, error } = await supabase
          .from('products')
          .insert({
            code: data.code,
            slug: data.slug,
            name: data.name,
            category_id: data.category_id,
            description: data.description || null,
            is_active: data.is_active,
            sort_order: data.sort_order,
            meta_title: seoData.meta_title || null,
            meta_description: seoData.meta_description || null,
            meta_keywords: seoData.meta_keywords || null,
            og_image: seoData.og_image || null,
          })
          .select()
          .single()
        if (error) throw error
        productId = newProduct.id
      }

      // Update features
      await supabase.from('product_features').delete().eq('product_id', productId)
      if (data.features.length > 0) {
        await supabase.from('product_features').insert(
          data.features.map((f, idx) => ({
            product_id: productId,
            feature: f.feature,
            sort_order: idx,
          }))
        )
      }

      // Update filters
      await supabase.from('product_filters').delete().eq('product_id', productId)
      const filterInserts: { product_id: string; filter_type: string; filter_value: string }[] = []
      Object.entries(selectedFilters).forEach(([type, values]) => {
        values.forEach((value) => {
          filterInserts.push({
            product_id: productId!,
            filter_type: type,
            filter_value: value,
          })
        })
      })
      if (filterInserts.length > 0) {
        await supabase.from('product_filters').insert(filterInserts)
      }

      // Delete removed images from storage and database
      for (const img of imagesToDelete) {
        // Extract path from URL
        const urlParts = img.url.split('/storage/v1/object/public/products/')
        if (urlParts[1]) {
          try {
            await deleteFile('products', urlParts[1])
          } catch (e) {
            console.warn('Failed to delete file from storage:', e)
          }
        }
        // Delete from database
        await supabase.from('product_images').delete().eq('id', img.id)
      }

      // Handle images
      for (const img of images) {
        if (img.file) {
          // Upload new image
          const path = `products/${productId}/${Date.now()}-${img.file.name}`
          await uploadFile('products', path, img.file)
          const url = getPublicUrl('products', path)
          await supabase.from('product_images').insert({
            product_id: productId,
            image_url: url,
            is_primary: img.is_primary,
            sort_order: images.indexOf(img),
          })
        } else if (img.id) {
          // Update existing
          await supabase
            .from('product_images')
            .update({ is_primary: img.is_primary, sort_order: images.indexOf(img) })
            .eq('id', img.id)
        }
      }

      return productId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      queryClient.invalidateQueries({ queryKey: ['admin-product', id] })
      setImagesToDelete([]) // Reset deleted images list
      navigate('/admin/products')
    },
  })

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const newImages = Array.from(files).map((file, idx) => ({
      url: URL.createObjectURL(file),
      is_primary: images.length === 0 && idx === 0,
      file,
    }))

    setImages([...images, ...newImages])
  }

  const removeImage = (index: number) => {
    const imgToRemove = images[index]
    // If image has an ID, it's from database - track it for deletion
    if (imgToRemove.id) {
      setImagesToDelete(prev => [...prev, { id: imgToRemove.id!, url: imgToRemove.url }])
    }
    const newImages = [...images]
    newImages.splice(index, 1)
    if (newImages.length > 0 && !newImages.some((img) => img.is_primary)) {
      newImages[0].is_primary = true
    }
    setImages(newImages)
  }

  const setPrimaryImage = (index: number) => {
    setImages(images.map((img, idx) => ({ ...img, is_primary: idx === index })))
  }

  const downloadImage = async (url: string, filename?: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename || url.split('/').pop() || 'image.jpg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  const toggleFilter = (type: string, value: string) => {
    setSelectedFilters((prev) => {
      const current = prev[type] || []
      if (current.includes(value)) {
        return { ...prev, [type]: current.filter((v) => v !== value) }
      }
      return { ...prev, [type]: [...current, value] }
    })
  }

  // Group filter definitions by type
  const groupedFilters = filterDefinitions?.reduce((acc, f) => {
    if (!acc[f.type]) acc[f.type] = []
    acc[f.type].push(f)
    return acc
  }, {} as Record<string, FilterDefinition[]>) || {}

  // DnD sensors for features
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle feature drag end
  const handleFeatureDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = featureFields.findIndex((f) => f.id === active.id)
      const newIndex = featureFields.findIndex((f) => f.id === over.id)

      // Get current features values
      const currentFeatures = watch('features')
      const reorderedFeatures = arrayMove(currentFeatures, oldIndex, newIndex).map((f, idx) => ({
        ...f,
        sort_order: idx,
      }))

      // Update the form with reordered features
      setValue('features', reorderedFeatures)
    }
  }

  // Handle image drag end
  const handleImageDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((_, idx) => `image-${idx}` === active.id)
      const newIndex = images.findIndex((_, idx) => `image-${idx}` === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedImages = arrayMove(images, oldIndex, newIndex)
        setImages(reorderedImages)
      }
    }
  }


  // Check if feature has been modified
  const isFeatureModified = (idx: number) => {
    const currentValue = watch(`features.${idx}.feature`)
    const originalValue = originalFeatures[idx]
    return originalValue !== undefined && currentValue !== originalValue
  }

  // Update original feature value (mark as saved)
  const markFeatureAsSaved = (idx: number) => {
    const currentValue = watch(`features.${idx}.feature`)
    setOriginalFeatures(prev => ({ ...prev, [idx]: currentValue }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/products')}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">
              {isEditing ? 'Edit Product' : 'New Product'}
            </h1>
            <p className="text-secondary-600">
              {isEditing ? 'Update product information' : 'Add a new product to catalog'}
            </p>
          </div>
        </div>
        {isEditing && existingProduct && (
          <a
            href={`/products/${categories?.find(c => c.id === existingProduct.category_id)?.slug || 'category'}/${existingProduct.slug}?preview=true`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Preview
          </a>
        )}
      </div>

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="card p-6 space-y-4">
              <h2 className="font-semibold text-secondary-900">Basic Information</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Product Code *
                  </label>
                  <input {...register('code')} className="input" placeholder="DOOR-WT" />
                  {errors.code && (
                    <p className="text-sm text-red-500 mt-1">{errors.code.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Slug *
                  </label>
                  <input {...register('slug')} className="input" placeholder="watertight-door" />
                  {errors.slug && (
                    <p className="text-sm text-red-500 mt-1">{errors.slug.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Product Name *
                </label>
                <input {...register('name')} className="input" placeholder="Watertight Door" />
                {errors.name && (
                  <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Category *
                </label>
                <select {...register('category_id')} className="input">
                  <option value="">Select category</option>
                  {categories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && (
                  <p className="text-sm text-red-500 mt-1">{errors.category_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Description
                </label>
                <RichTextEditor
                  content={watch('description') || ''}
                  onChange={(html) => setValue('description', html)}
                  placeholder="Product description..."
                />
              </div>
            </div>

            {/* SEO */}
            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Search className="w-5 h-5 text-primary-600" />
                <h2 className="font-semibold text-secondary-900">SEO Settings</h2>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-medium text-secondary-700 mb-1">
                  Meta Title
                  <span className={`text-xs ${(seoData.meta_title?.length || 0) > 60 ? 'text-red-500' : 'text-secondary-400'}`}>
                    {seoData.meta_title?.length || 0}/60
                  </span>
                </label>
                <input
                  type="text"
                  value={seoData.meta_title}
                  onChange={(e) => setSeoData({ ...seoData, meta_title: e.target.value })}
                  className="input"
                  placeholder="Product title for search engines..."
                />
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-medium text-secondary-700 mb-1">
                  Meta Description
                  <span className={`text-xs ${(seoData.meta_description?.length || 0) > 160 ? 'text-red-500' : 'text-secondary-400'}`}>
                    {seoData.meta_description?.length || 0}/160
                  </span>
                </label>
                <textarea
                  value={seoData.meta_description}
                  onChange={(e) => setSeoData({ ...seoData, meta_description: e.target.value })}
                  className="input h-24 resize-none"
                  placeholder="Brief description for search results..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Meta Keywords
                </label>
                <textarea
                  value={seoData.meta_keywords}
                  onChange={(e) => setSeoData({ ...seoData, meta_keywords: e.target.value })}
                  className="input h-16 resize-none"
                  placeholder="keyword1, keyword2, keyword3..."
                />
                <p className="text-xs text-secondary-500 mt-1">Comma-separated list of keywords</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  OG Image URL
                </label>
                <input
                  type="text"
                  value={seoData.og_image}
                  onChange={(e) => setSeoData({ ...seoData, og_image: e.target.value })}
                  className="input"
                  placeholder="https://... (for social media sharing)"
                />
              </div>
            </div>

            {/* Images */}
            <div className="card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-secondary-900">Images</h2>
                <span className="text-xs text-secondary-500">Drag to reorder</span>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleImageDragEnd}
              >
                <SortableContext
                  items={images.map((_, idx) => `image-${idx}`)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {images.map((img, idx) => (
                      <SortableImageItem
                        key={`image-${idx}`}
                        id={`image-${idx}`}
                        image={img}
                        index={idx}
                        onSetPrimary={setPrimaryImage}
                        onDownload={downloadImage}
                        onRemove={removeImage}
                      />
                    ))}
                    <label className="aspect-square rounded-lg border-2 border-dashed border-secondary-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 transition-colors">
                      <Upload className="w-8 h-8 text-secondary-400 mb-2" />
                      <span className="text-sm text-secondary-500">Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="sr-only"
                      />
                    </label>
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            {/* Features */}
            <div className="card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-secondary-900">Technical Features</h2>
                <button
                  type="button"
                  onClick={() => {
                    appendFeature({ feature: '', sort_order: featureFields.length })
                    setTimeout(() => {
                      const inputs = document.querySelectorAll<HTMLInputElement>('[data-feature-input]')
                      const lastInput = inputs[inputs.length - 1]
                      lastInput?.focus()
                    }, 50)
                  }}
                  className="btn-outline btn-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Feature
                </button>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleFeatureDragEnd}
              >
                <SortableContext
                  items={featureFields.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {featureFields.map((field, idx) => (
                      <SortableFeatureItem
                        key={field.id}
                        id={field.id}
                        idx={idx}
                        register={register}
                        onRemove={removeFeature}
                        isModified={isFeatureModified(idx)}
                        onSave={markFeatureAsSaved}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {featureFields.length === 0 && (
                <p className="text-sm text-secondary-500 text-center py-4">
                  No features added. Click "Add Feature" to add one.
                </p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="card p-6 space-y-4">
              <h2 className="font-semibold text-secondary-900">Status</h2>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('is_active')}
                  className="w-5 h-5 rounded border-secondary-300"
                />
                <span className="text-sm text-secondary-700">Product is active</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  {...register('sort_order', { valueAsNumber: true })}
                  className="input"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="card p-6 space-y-4">
              <h2 className="font-semibold text-secondary-900">Filters</h2>
              {Object.entries(groupedFilters).map(([type, defs]) => (
                <div key={type}>
                  <h3 className="text-sm font-medium text-secondary-700 mb-2 capitalize">
                    {type.replace('_', ' ')}
                  </h3>
                  <div className="space-y-2">
                    {defs.map((def) => (
                      <label key={def.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedFilters[type]?.includes(def.value) || false}
                          onChange={() => toggleFilter(type, def.value)}
                          className="w-4 h-4 rounded border-secondary-300"
                        />
                        <span className="text-sm text-secondary-600">{def.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="card p-6">
              <button
                type="submit"
                disabled={isSubmitting || saveMutation.isPending}
                className="btn-primary w-full"
              >
                <Save className="w-5 h-5 mr-2" />
                {isSubmitting || saveMutation.isPending ? 'Saving...' : 'Save Product'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
